import { ActionFunctionArgs, HeadersFunction, LoaderFunctionArgs, MetaArgs, json, redirect } from '@remix-run/node';
import { useFetcher, useLoaderData } from '@remix-run/react';
import {
	GQLProductSideSideType,
	GQLProductStatusType,
	GQLProductTileFragment,
	GetAllProductsForSitemapDocument,
	GetCartItemDocument,
	GetDesignPageDocument,
	GetMerchTileDocument,
	GetOperationalSettingsDocument,
	GetProductDocument,
	GetQuoteItemDocument,
	GetWebsiteRedirectsDocument,
} from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonHour, getSessionQuote, serverEnv, toastError, withEnsuredForm } from '@theprintbar/server';
import {
	DesignParamAction,
	DesignParamKeys,
	WebRoutes,
	getDesignParams,
	getSortedSides,
	invariantError,
	invariantNotFound,
	merchFormServerValidator,
	withDomain,
} from '@theprintbar/shared';
import { DesignPage, editorHeaders, gaCustomEvent } from '@theprintbar/ui';
import { useEffect } from 'react';
import { SitemapFunction } from 'remix-sitemap';
import { serverOnly$ } from 'vite-env-only/macros';
import { getLoggedInUserId } from '~/server/auth.server';
import { contentMeta } from '~/utils';
import { addMerchToCart } from '../addMerch.server';
import { getProductStructuredData } from '../utils/getProductStructuredData';

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export function meta({ data, matches }: MetaArgs<typeof loader>) {
	if (!data) return [];

	return [...contentMeta({ data: data.product, matches }), getProductStructuredData(data)];
}

export const sitemap: SitemapFunction = serverOnly$(async ({}) => {
	const { products } = await runSudoGraphql(GetAllProductsForSitemapDocument);

	return (
		products?.reduce((acc, { slug, updatedAt }) => {
			return [
				...acc,
				{
					loc: WebRoutes.Product(slug),
					lastmod: new Date(updatedAt).toISOString(),
					priority: 0.5,
				},
			];
		}, [] as any[]) || []
	);
})!;

export async function action({ request, params }: ActionFunctionArgs) {
	const { productSlug } = params;
	invariantNotFound(productSlug, 'No slug provided');

	const { product } = await runSudoGraphql(GetProductDocument, { where: { slug: productSlug } });
	invariantError(product, 'No product found');

	const user = await getLoggedInUserId(request);

	return await withEnsuredForm(
		{
			validator: merchFormServerValidator,
			request,
		},
		async form => {
			const { error, headers, line } = await addMerchToCart(request, product, form, user);
			if (error) {
				return toastError({
					title: 'Uh Oh!',
					message: error,
					requireClose: true,
				});
			}

			return form.action === DesignParamAction.Edit
				? redirect(
						WebRoutes.Product(product.slug, {
							ref: line?.id,
							action: DesignParamAction.Edit,
						}),
				  )
				: json({ success: !!line?.id, cartItemID: line?.id }, headers);
		},
	);
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	invariantNotFound(params.productSlug, 'No product slug provided');
	const merchParams = getDesignParams(request);
	let slug = params.productSlug;

	const [{ designPage }, { product }, { operationalSettings }, quoteSession] = await Promise.all([
		runSudoGraphql(GetDesignPageDocument),
		runSudoGraphql(GetProductDocument, {
			where: {
				slug,
			},
		}),
		runSudoGraphql(GetOperationalSettingsDocument),
		getSessionQuote(request),
	]);

	if (!product) {
		const { websiteRedirects } = await runSudoGraphql(GetWebsiteRedirectsDocument, {
			where: { from: { equals: `/products/${slug}` } },
		});

		if (websiteRedirects?.[0]?.to) {
			return redirect(`${websiteRedirects[0].to}${new URL(request.url).search}`, 301);
		}
		invariantNotFound(product, 'No product found');
	}

	invariantNotFound(designPage, 'No Design page content found');
	invariantNotFound(product.status === GQLProductStatusType.Published && product.availableWeb, 'No product found');
	invariantNotFound(operationalSettings, 'No operational settings found');
	product.sides = getSortedSides(product).filter(side => side.side !== GQLProductSideSideType.Label);

	const cartItem = merchParams.ref
		? quoteSession
			? (await runSudoGraphql(GetQuoteItemDocument, { where: { id: merchParams.ref } })).quoteItem
			: (await runSudoGraphql(GetCartItemDocument, { where: { id: merchParams.ref } })).cartItem
		: null;

	if (merchParams.ref && !cartItem) {
		return redirect(WebRoutes.Product(slug));
	}

	const referenceMerch = merchParams.merch
		? (await runSudoGraphql(GetMerchTileDocument, { where: { id: merchParams.merch } })).merchItem
		: cartItem?.merch;

	if (cartItem?.lines?.length) {
		merchParams[DesignParamKeys.Sizes] = cartItem.lines.map(line => ({
			size: line.variant.size.code,
			quantity: line.quantity,
		}));
		merchParams[DesignParamKeys.TeamnameId] = cartItem.teamNameId || undefined;
	}

	return cachedJsonHour(
		{
			params: merchParams,
			referenceMerch,
			designHandlerUrl: withDomain(WebRoutes.API.commerce.userDesigns, serverEnv.WEB_DOMAIN),
			content: designPage,
			product,
			operationalSettings,
			recaptchaKey: serverEnv.RECAPTCHA_SITE_KEY,
			webDomain: serverEnv.WEB_DOMAIN,
			licence: serverEnv.IMGLY_KEY,
			assetsUrl: serverEnv.ASSET_DOMAIN,
			cesdkAssetsUrl: serverEnv.S3_ASSETS_URL,
			quoteSession,
		},
		{
			headers: editorHeaders,
		},
	);
}

export default function () {
	const { quoteSession, ...data } = useLoaderData<typeof loader>();
	const bestSellers = useFetcher<{ products: GQLProductTileFragment[] }>();

	useEffect(() => {
		bestSellers.load(WebRoutes.API.commerce.products.related(data.product.slug, data.product.mainCategory?.[0]?.code || ''));

		gaCustomEvent({
			action: 'view_item',
			params: {
				currency: 'AUD',
				value: data.product.salePrice,
			},
			items: [
				{
					id: data.product,
					name: data.product.name,
					brand: data.product.brand.name,
					price: data.product.salePrice,
					quantity: 1,
				},
			],
		});
	}, []);

	return (
		<div className="max-w-8xl w-full mx-auto">
			<DesignPage
				isQuote={!!quoteSession}
				bestSellers={bestSellers.data?.products || []}
				{...data}
				tutorialKey="web-designer"
				className="lg:px-8 px-2.5 py-4 lg:py-8"
			/>
		</div>
	);
}
